import { ConditionalRender, InfoBanner } from '@ui';
import { useContext, useMemo } from 'react';
import { AuthContext } from '@lp/core/auth';
import { CaseDeviceTrialBanners } from '@lp/features/CaseDeviceTrialBanners/CaseDeviceTrialBanners';
import { TmdxResourceCard } from '@features/components/ResourceCards';
import { ScsUrl } from '@icons';
import { EmployeeRole } from '@/api/apis/resources';
import { ResourceSection } from '../ResourceSection';
import {
  Case,
  CaseClinicalSpecialistResource,
  CaseOCSConsoleResource,
  CaseResource,
  CaseResourceType,
} from '@/api/apis/case';
import { NoContacts } from '../NoContacts';
import { Uuid } from '@/api/types';
import { ClinicalEmployeeCard } from '../ClinicalEmployeeCard';
import { CaseDetailsPermissions } from '@/permissions';
import { OcsDeviceCard } from '../OcsDeviceCard';
import { useCaseDetails } from '@/hooks/useCaseDetails';
import { getClinicalResourceMismatchWarnings, isRandomizationScs } from '@/utils/helpers';
import { useClinicalResourceTrialPrograms } from '@/hooks/useClinicalResourceTrialPrograms';
import { sortClinicalSpecialistsByEmployeeLabel } from '@/utils/helpers/resources.helpers';

type ClinicalResourcesProps = {
  caseId: Uuid;
};

export const ClinicalResources = ({ caseId }: ClinicalResourcesProps) => {
  const { checkPermission } = useContext(AuthContext);
  const caseData = useCaseDetails() as Case;
  const hasResourcesStatusUpdatePermission = checkPermission(CaseDetailsPermissions.ResourceStatusUpdate);

  const clinicalSpecialists = useMemo(
    () =>
      sortClinicalSpecialistsByEmployeeLabel(
        caseData.resources.filter(
          (resource): resource is CaseClinicalSpecialistResource =>
            resource.resourceType === CaseResourceType.ClinicalSpecialist,
        ),
      ),
    [caseData.resources],
  );

  const retrievalSurgeons = useMemo(
    () =>
      caseData.resources.filter(
        (resource): false | true | undefined =>
          resource.resourceType === CaseResourceType.Surgeon &&
          !!resource.resourceData?.roles.includes(EmployeeRole.RetrievalSurgeon),
      ),
    [caseData.resources],
  );

  const contractorSurgeons = useMemo(
    () =>
      caseData.resources.filter(
        (resource): false | true | undefined =>
          resource.resourceType === CaseResourceType.Surgeon &&
          !!resource.resourceData?.roles.includes(EmployeeRole.ContractorRetrievalSurgeon),
      ),
    [caseData.resources],
  );

  const ocsConsoles = useMemo(
    () =>
      caseData.resources.filter(
        (resource): resource is CaseResource<CaseOCSConsoleResource> =>
          resource.resourceType === CaseResourceType.OCSConsole,
      ),
    [caseData.resources],
  );

  const { clinicalSpecialistTrialProgramsById, ocsConsoleTrialProgramsById } = useClinicalResourceTrialPrograms({
    clinicalSpecialists,
    ocsConsoles,
  });

  const resourcesLength = clinicalSpecialists.length + retrievalSurgeons.length + ocsConsoles.length;
  const isRandomizedSCS = isRandomizationScs(caseData.randomization);

  const mismatchWarnings = useMemo(() => {
    return getClinicalResourceMismatchWarnings(caseData, {
      clinicalSpecialistTrialProgramsById,
      ocsConsoleTrialProgramsById,
    });
  }, [caseData, clinicalSpecialistTrialProgramsById, ocsConsoleTrialProgramsById]);

  return (
    <ResourceSection title="Clinical Resources" count={resourcesLength}>
      {isRandomizedSCS && (
        <div className="w-full">
          <InfoBanner iconType="infoWarning" withClose>
            In accordance with the randomization results, the organ must be placed into static cold storage.
          </InfoBanner>
        </div>
      )}

      {mismatchWarnings.map((warning) => (
        <div className="w-full" key={warning.id}>
          <InfoBanner iconType="warning">{warning.message}</InfoBanner>
        </div>
      ))}

      <CaseDeviceTrialBanners caseTrialProgram={caseData.trialProgram} resources={caseData.resources} isCaseDetails />

      <ConditionalRender show={!!resourcesLength} placeholder={<NoContacts />}>
        {clinicalSpecialists.map(({ logisticsStatus, resourceId, resourceData, data }) => (
          <ClinicalEmployeeCard
            caseData={caseData}
            menuDisabled={!hasResourcesStatusUpdatePermission}
            key={resourceId}
            caseId={caseId}
            id={resourceId}
            employee={resourceData}
            shadowOrgans={data?.shadowOrganSpecializations}
            proctorOrgans={data?.proctorOrganSpecializations}
            logisticsStatus={logisticsStatus}
            role={EmployeeRole.ClinicalSpecialist}
          />
        ))}

        {retrievalSurgeons.map(({ logisticsStatus, resourceId, resourceData }) => (
          <ClinicalEmployeeCard
            caseData={caseData}
            menuDisabled={!hasResourcesStatusUpdatePermission}
            key={resourceId}
            caseId={caseId}
            id={resourceId}
            employee={resourceData}
            logisticsStatus={logisticsStatus}
            role={EmployeeRole.RetrievalSurgeon}
          />
        ))}

        {contractorSurgeons.map(({ logisticsStatus, resourceId, resourceData }) => (
          <ClinicalEmployeeCard
            caseData={caseData}
            menuDisabled={!hasResourcesStatusUpdatePermission}
            key={resourceId}
            caseId={caseId}
            id={resourceId}
            employee={resourceData}
            logisticsStatus={logisticsStatus}
            role={EmployeeRole.ContractorRetrievalSurgeon}
          />
        ))}

        {ocsConsoles.map(({ logisticsStatus, resourceId }) => (
          <OcsDeviceCard key={resourceId} logisticsStatus={logisticsStatus} deviceId={resourceId} caseId={caseId} />
        ))}

        {isRandomizedSCS && (
          <div className="w-80">
            <TmdxResourceCard
              list={[]}
              menuDisabled
              type="device"
              title="Static Cold Storage"
              avatarProps={{
                size: 'xl',
                type: 'device',
                className: 'p-1',
                src: ScsUrl,
              }}
              showChip={false}
            />
          </div>
        )}
      </ConditionalRender>
    </ResourceSection>
  );
};
